import { useState, useCallback } from 'react';
import { Check, Copy, KeyRound } from 'lucide-react';
import { cn } from '../../lib/utils';

interface LookupCodePillProps {
  code: string;
  /** `compact` (default) suits desktop nav bars — icon + mono code in a pill.
   *  `full` is used inside the mobile menu drawer where we have more room
   *  and want an explicit label so the golfer understands what the code is
   *  for before they tap it. */
  variant?: 'compact' | 'full';
  className?: string;
}

/**
 * Small clipboard pill that renders the golfer's profile lookup code in the
 * nav bar. The whole pill is the click target; on click it copies the code,
 * flashes a `Copied` confirmation for ~1.5 s, then reverts. No alert dialog
 * (unlike the Profile page card) — the nav is a glance surface, not a
 * modal-heavy one.
 */
export function LookupCodePill({
  code,
  variant = 'compact',
  className,
}: LookupCodePillProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // If clipboard API is blocked (e.g. iframe without permission), fall
      // back to a one-off prompt so the user can still grab the code.
      window.prompt('Copy your lookup code:', code);
    }
  }, [code]);

  if (!code) return null;

  if (variant === 'full') {
    return (
      <button
        type="button"
        onClick={handleCopy}
        className={cn(
          'flex items-center gap-2 py-3 px-4 rounded-lg transition-colors border',
          'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10',
          'border-brand-500/30',
          className,
        )}
        aria-label={`Copy your profile lookup code ${code}`}
      >
        <KeyRound className="w-4 h-4 shrink-0 text-brand-500" />
        <span className="flex-1 text-left text-sm font-medium">
          Profile code
        </span>
        <span className="font-mono text-sm font-semibold text-brand-600 dark:text-brand-400 tracking-wider">
          {code}
        </span>
        {copied ? (
          // Brand v1.0: cyan (--color-accent) is the single permitted
          // positive/focal accent. Was emerald-500 (off-palette).
          <Check
            className="w-4 h-4 shrink-0"
            style={{ color: 'var(--color-accent, #00ADED)' }}
          />
        ) : (
          <Copy className="w-4 h-4 text-gray-400 dark:text-slate-400 shrink-0" />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors',
        'border-brand-500/30 bg-brand-500/10 hover:bg-brand-500/20',
        'text-brand-700 dark:text-brand-300',
        className,
      )}
      title={copied ? 'Copied!' : 'Click to copy your profile lookup code'}
      aria-label={`Copy your profile lookup code ${code}`}
    >
      <KeyRound className="w-3.5 h-3.5 shrink-0" />
      <span className="font-mono text-xs font-bold tracking-wider">
        {code}
      </span>
      {copied ? (
        <Check
          className="w-3.5 h-3.5 shrink-0"
          style={{ color: 'var(--color-accent, #00ADED)' }}
        />
      ) : (
        <Copy className="w-3.5 h-3.5 shrink-0 opacity-70" />
      )}
    </button>
  );
}
